/**
 * Incoming Matrix call modal.
 * Shown when Call.incoming fires; user can Answer or Reject.
 * On answer, navigates to /call and uses activeCall there.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'reactstrap';
import { MatrixCallContext } from '../../../../MatrixCallProvider';
import { MatrixContext } from '../../../../MatrixProvider';
import { useNavigation } from '../../../../shared/utils/useNavigation';
import {
  DEFAULT_AVATAR,
  memberDisplayNameForUi
} from '../../../../shared/utils/matrix/matrixRoomUtils';
import { fetchMatrixRooms } from '../../../../shared/actions/matrixAction';
import { fetchWalletBalanceAction } from '../../../../shared/actions/creditAction';
import { ROLE } from '../../../../shared/constants/common';
import { getUserRole } from '../../../../shared/utils/function';
import { useMatrixRoomInfo } from '../../../../shared/utils/matrix/useMatrixRoomInfo';
import {
  formatCreditsValue,
  getCallRequestPricingDetails,
  isWalletBalancePending,
  subscriberHasMinimumCreditsForCall,
} from './utils/callFormatUtils';
import {
  isCallRingtoneAwaitingGesture,
  subscribeCallRingtoneState,
  unlockCallRingtoneAudio,
} from '../../../../shared/services/call/callRingtoneService';
import { bootstrapIncomingCallAnswer } from '../../../../shared/services/call/callRouteBootstrap';
import { findLatestCallRateOffer, mergeCallRateIntoCreatorInfo } from './utils/callRateOfferUtils';
import { setCallFlowActive } from '../../../../shared/utils/config/paymentFlowGuard';

const hasActiveVideoMLine = (sdp = '') => {
  if (typeof sdp !== 'string' || !sdp) return false;
  const lines = sdp.split(/\r?\n/);
  return lines.some((line) => {
    const trimmed = line.trim().toLowerCase();
    if (!trimmed.startsWith('m=video')) return false;
    const parts = trimmed.split(/\s+/);
    const port = Number(parts[1]);
    // In SDP, m=video 0 means media is rejected/disabled.
    if (Number.isNaN(port)) return true;
    return port > 0;
  });
};

const getCallMediaType = (call) => {
  if (!call) return 'voice';

  if (call?.type === 'voice' || call?.type === 'video') return call.type;

  if (call?.hasLocalVideoTrack === true || call?.hasRemoteVideoTrack === true) return 'video';
  if (call?.hasLocalVideoTrack === false && call?.hasRemoteVideoTrack === false) return 'voice';

  const hasVideoTracks = Boolean(
    call?.localUsermediaStream?.getVideoTracks?.()?.length ||
    call?.remoteUsermediaStream?.getVideoTracks?.()?.length
  );
  if (hasVideoTracks) return 'video';

  const sdpCandidates = [
    call?.offerSdp,
    call?.inviteeSdp,
    call?._remoteDescription?.sdp,
    call?._localDescription?.sdp,
    call?.peerConn?._remoteDescription?.sdp,
    call?.peerConn?.remoteDescription?.sdp,
  ].filter((value) => typeof value === 'string' && value.length > 0);

  const hasVideoInSdp = sdpCandidates.some((sdp) => hasActiveVideoMLine(sdp));
  if (hasVideoInSdp) return 'video';

  return 'voice';
};

const IncomingCallModal = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const { client } = useContext(MatrixContext);
  const { incomingCall, answerCall, rejectCall, reportMediaError } =
    useContext(MatrixCallContext);
  const matrixState = useSelector((state) => state.matrix);
  const walletBalance = useSelector((state) => state.credit?.walletBalance);
  const walletBalanceLoading = useSelector((state) => state.credit?.walletBalanceLoading);
  const walletBalanceError = useSelector((state) => state.credit?.walletBalanceError);
  const currentUser = useSelector((state) => state.user?.user);
  const matrixRooms = matrixState?.rooms;
  const matrixLoading = matrixState?.loading;
  const hasRoomsLoaded = matrixState?.hasRoomsLoaded;

  const isOpen = Boolean(incomingCall);
  const [awaitingRingtoneGesture, setAwaitingRingtoneGesture] = useState(
    () => isCallRingtoneAwaitingGesture()
  );

  useEffect(() => {
    if (!incomingCall) {
      setAwaitingRingtoneGesture(false);
      return undefined;
    }
    const sync = () => setAwaitingRingtoneGesture(isCallRingtoneAwaitingGesture());
    sync();
    return subscribeCallRingtoneState(sync);
  }, [incomingCall]);

  // Block signed-URL refresh while an incoming call is ringing (before /call mounts).
  useEffect(() => {
    if (!incomingCall) return undefined;
    setCallFlowActive(true);
    return () => setCallFlowActive(false);
  }, [incomingCall]);

  /**
   * Defensive cleanup for modal artifacts that can survive rapid call-end transitions.
   * If Bootstrap's modal-open/backdrop state gets stuck, the whole app becomes unclickable
   * until a hard refresh. Only clear global modal locks when no visible modal remains.
   */
  useEffect(() => {
    if (isOpen) return undefined;

    const cleanupId = window.setTimeout(() => {
      const hasVisibleModal = Boolean(document.querySelector('.modal.show'));
      if (hasVisibleModal) return;

      document.querySelectorAll('.modal-backdrop').forEach((backdropEl) => {
        backdropEl.remove();
      });

      document.body.classList.remove('modal-open');
      document.documentElement.classList.remove('modal-open');

      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      document.documentElement.style.removeProperty('overflow');
    }, 0);

    return () => window.clearTimeout(cleanupId);
  }, [isOpen]);

  // Incoming calls may appear before the message module has fetched room metadata.
  // Fetch rooms on-demand so `otherUser.username` + `otherUser.profile_picture` are available.
  useEffect(() => {
    if (!incomingCall) return;
    if (hasRoomsLoaded) return;
    if (matrixLoading) return;
    dispatch(fetchMatrixRooms());
  }, [incomingCall, dispatch, hasRoomsLoaded, matrixLoading]);

  const incomingRoomMetadata = useMemo(() => {
    if (!incomingCall?.roomId) return null;
    const rooms = Array.isArray(matrixRooms?.rooms) ? matrixRooms.rooms : [];
    return rooms.find((room) => room?.roomid === incomingCall.roomId) || null;
  }, [incomingCall, matrixRooms]);

  const normalizeMaybeUserId = (value) => {
    if (Array.isArray(value)) return value.find((v) => typeof v === 'string' && v) ?? null;
    if (typeof value === 'string') return value;
    return value?.userId ?? null;
  };

  const opponentContext = useMemo(() => {
    if (!incomingCall || !client) {
      return { member: null, userId: null };
    }

    try {
      const room = client.getRoom?.(incomingCall.roomId);
      const myUserId = client.getUserId?.();
      const directMember = incomingCall.getOpponentMember?.() || null;
      const directUserId =
        normalizeMaybeUserId(directMember?.userId) ||
        normalizeMaybeUserId(incomingCall.getOpponentUserId?.()) ||
        normalizeMaybeUserId(incomingCall.opponentUserId) ||
        normalizeMaybeUserId(incomingCall.invitee) ||
        normalizeMaybeUserId(incomingCall.callerId) ||
        null;

      const stateMembers = [
        ...(room?.getJoinedMembers?.() || []),
        ...(room?.getMembers?.() || []),
        ...(room?.currentState?.getMembers?.() || []),
      ]
        .filter((member) => member?.userId)
        .filter(
          (member, index, arr) =>
            arr.findIndex((candidate) => candidate?.userId === member?.userId) === index
        );

      const memberFromId = directUserId
        ? room?.getMember?.(directUserId) ||
        stateMembers.find((member) => member?.userId === directUserId) ||
        null
        : null;

      const fallbackMember =
        stateMembers.find((member) => member?.userId && member.userId !== myUserId) || null;

      const resolvedMember = directMember || memberFromId || fallbackMember;
      const resolvedUserId = resolvedMember?.userId || directUserId || fallbackMember?.userId || null;

      return { member: resolvedMember, userId: resolvedUserId };
    } catch {
      return { member: null, userId: null };
    }
  }, [incomingCall, client]);

  const opponentDisplayName = useMemo(() => {
    if (!incomingCall) return null;
    try {
      const { member, userId } = opponentContext;
      const metadataName = incomingRoomMetadata?.otherUser?.username;
      if (metadataName) return metadataName;
      const name = memberDisplayNameForUi(member) || member?.rawDisplayName || member?.name || member?.userId;
      if (name) return name;
      if (!userId) return 'Unknown';
      if (userId.includes(':')) {
        return `@${userId.split(':')[0]?.replace(/^@/, '')}`;
      }
      return userId;
    } catch {
      return 'Unknown';
    }
  }, [incomingCall, opponentContext, incomingRoomMetadata]);

  /** Pill label: @handle style, lowercase (Matrix display names may be mixed case). */
  const opponentPillUsername = useMemo(() => {
    const raw = opponentDisplayName;
    if (raw == null || String(raw).trim() === '') return '@unknown';
    const trimmed = String(raw).trim();
    const withoutAt = trimmed.startsWith('@') ? trimmed.slice(1) : trimmed;
    const lower = withoutAt.toLowerCase();
    return `@${lower || 'unknown'}`;
  }, [opponentDisplayName]);

  const opponentAvatar = useMemo(() => {
    if (!incomingCall || !client) return DEFAULT_AVATAR;
    try {
      return (
        incomingRoomMetadata?.otherUser?.profile_picture ||
        incomingRoomMetadata?.otherUser?.profilePicture ||
        opponentContext.member?.getAvatarUrl?.(client.baseUrl, 112, 112, 'scale', true) ||
        DEFAULT_AVATAR
      );
    } catch {
      return DEFAULT_AVATAR;
    }
  }, [incomingCall, client, opponentContext, incomingRoomMetadata]);

  const incomingCallMediaType = useMemo(() => getCallMediaType(incomingCall), [incomingCall]);
  const isIncomingVideoCall = incomingCallMediaType === 'video';

  const isSubscriber = getUserRole() === ROLE.USER;
  const opponentBackendUserId = useMemo(() => {
    if (!incomingRoomMetadata?.otherUser) return null;
    const ou = incomingRoomMetadata.otherUser;
    return ou._id || ou.id || null;
  }, [incomingRoomMetadata]);

  const { destUserInfo } = useMatrixRoomInfo(isSubscriber ? opponentBackendUserId : null);

  const creatorForIncomingPricing = useMemo(
    () => destUserInfo || incomingRoomMetadata?.otherUser || null,
    [destUserInfo, incomingRoomMetadata?.otherUser]
  );

  const incomingRateOverride = useMemo(() => {
    if (!incomingCall?.roomId || !client) return null;
    const room = client.getRoom?.(incomingCall.roomId);
    return findLatestCallRateOffer({
      matrixRoom: room,
      mediaType: isIncomingVideoCall ? 'video' : 'voice',
      maxAgeMs: 2 * 60 * 1000,
      expectedSenderUserId: opponentContext?.userId || null,
    });
  }, [incomingCall, client, isIncomingVideoCall, opponentContext?.userId]);

  const creatorForEffectiveIncomingPricing = useMemo(() => {
    return mergeCallRateIntoCreatorInfo({
      creatorInfo: creatorForIncomingPricing,
      mediaType: isIncomingVideoCall ? 'video' : 'voice',
      callRateOffer: incomingRateOverride,
    });
  }, [creatorForIncomingPricing, incomingRateOverride, isIncomingVideoCall]);

  const incomingPricingResult = useMemo(
    () =>
      getCallRequestPricingDetails(
        creatorForEffectiveIncomingPricing,
        isIncomingVideoCall ? 'video' : 'voice'
      ),
    [creatorForEffectiveIncomingPricing, isIncomingVideoCall]
  );
  const incomingPricingSummary = useMemo(() => {
    if (!isSubscriber || !incomingPricingResult?.ok) return null;
    if (incomingPricingResult.pricingModel === 'flat_rate') {
      return `$${incomingPricingResult.amountUsd} per ${incomingPricingResult.minDurationMins} min · ${incomingPricingResult.ratePerMinuteRow}/min`;
    }
    if (incomingPricingResult.hasMinimumDuration) {
      return `${incomingPricingResult.ratePerMinuteRow}/min (${incomingPricingResult.minDurationMins} min req)`;
    }
    return `${incomingPricingResult.ratePerMinuteRow}/min (no minimum)`;
  }, [incomingPricingResult, isSubscriber]);

  const creditsRequiredToAccept = useMemo(() => {
    if (!incomingPricingResult?.ok) return null;
    if (incomingPricingResult.pricingModel === 'flat_rate') {
      return incomingPricingResult.flatRateCredits ?? incomingPricingResult.minCostCredits;
    }
    return incomingPricingResult.minCostCredits;
  }, [incomingPricingResult]);

  const walletBalancePending = useMemo(
    () => isWalletBalancePending(walletBalance, walletBalanceLoading, walletBalanceError),
    [walletBalance, walletBalanceLoading, walletBalanceError]
  );

  // /call after hard refresh does not mount MessageHeader — credits were never requested.
  useEffect(() => {
    if (!incomingCall) return;
    if (!isSubscriber || !incomingPricingResult.ok) return;
    const subscriberId = currentUser?._id || currentUser?.id;
    dispatch(fetchWalletBalanceAction(subscriberId));
  }, [incomingCall, isSubscriber, incomingPricingResult.ok, dispatch, currentUser?._id, currentUser?.id]);

  const walletCredits = Number(walletBalance?.credits);
  const canSubscriberAnswerByBalance =
    !incomingPricingResult.ok ||
    subscriberHasMinimumCreditsForCall(
      incomingPricingResult,
      walletCredits,
      walletBalancePending
    );
  const canAnswer = !isSubscriber || canSubscriberAnswerByBalance;
  const insufficientCreditsForIncoming =
    isSubscriber &&
    incomingPricingResult.ok &&
    !walletBalancePending &&
    Number.isFinite(walletCredits) &&
    walletCredits < incomingPricingResult.minCostCredits;

  /** Inline answer control only — avoid a second-line "Checking…" flash on incoming calls. */
  const answerButtonCreditsPending =
    isSubscriber && incomingPricingResult.ok && walletBalancePending;

  const handleAnswer = useCallback(async () => {
    if (!canAnswer || !incomingCall) return;

    const callKind = getCallMediaType(incomingCall) === 'video' ? 'video' : 'voice';
    const isVideoIncoming = callKind === 'video';
    const incomingRoomId = incomingCall.roomId;

    const media = await bootstrapIncomingCallAnswer(callKind);
    if (!media?.ok) {
      reportMediaError?.(media?.error);
      const navigateToCall = isVideoIncoming ? navigate.toVideo : navigate.toCall;
      navigateToCall({
        roomId: incomingRoomId,
        userId:
          incomingRoomMetadata?.otherUser?.id ||
          incomingRoomMetadata?.otherUser?._id ||
          undefined,
        displayName: opponentDisplayName || undefined,
        avatar: opponentAvatar || undefined,
        callType: 'incoming',
        replace: true,
      });
      return;
    }

    const answerPromise = answerCall();
    // answerCall publishes activeCall synchronously before negotiation; yield so
    // CallingScreen can mount with the live session instead of a dead waiting state.
    await Promise.resolve();

    const navigateToCall = isVideoIncoming ? navigate.toVideo : navigate.toCall;
    navigateToCall({
      roomId: incomingRoomId,
      userId:
        incomingRoomMetadata?.otherUser?.id ||
        incomingRoomMetadata?.otherUser?._id ||
        undefined,
      displayName: opponentDisplayName || undefined,
      avatar: opponentAvatar || undefined,
      callType: 'incoming',
      replace: true,
    });

    const call = await answerPromise;
    if (!call) return;

    // Reconcile route media type if SDP reveals a different mode than the invite UI.
    const resolvedVideo = getCallMediaType(call) === 'video';
    if (resolvedVideo !== isVideoIncoming && call.roomId) {
      const reconcileNavigate = resolvedVideo ? navigate.toVideo : navigate.toCall;
      reconcileNavigate({
        roomId: call.roomId,
        userId:
          incomingRoomMetadata?.otherUser?.id ||
          incomingRoomMetadata?.otherUser?._id ||
          undefined,
        displayName: opponentDisplayName || undefined,
        avatar: opponentAvatar || undefined,
        callType: 'incoming',
        replace: true,
      });
    }
  }, [
    answerCall,
    canAnswer,
    navigate,
    incomingRoomMetadata,
    opponentDisplayName,
    opponentAvatar,
    incomingCall,
    reportMediaError,
  ]);

  const handleReject = useCallback(() => {
    rejectCall();
  }, [rejectCall]);

  const handleIncomingInteract = useCallback(() => {
    unlockCallRingtoneAudio();
  }, []);

  // Always show while Matrix has an invite so callee sees + hears the ring.
  if (!incomingCall) return null;

  const showTapForSoundHint = awaitingRingtoneGesture;

  return (
    <Modal
      isOpen={isOpen}
      modalClassName="no_blur_bg"
      className="cm_incoming_call_instagram_pill_wrapper"
      contentClassName="cm_incoming_call_instagram_pill_content"
      backdrop={false}
      fade={false}
    >
      <div
        className="cm_instagram_pill_body"
        onPointerDown={handleIncomingInteract}
        onTouchStart={handleIncomingInteract}
      >
        <div className="cm_instagram_pill_left">
          <div className="cm_instagram_pill_avatar_wrapper">
            <img
              src={opponentAvatar || DEFAULT_AVATAR}
              alt="Caller"
              className="cm_instagram_pill_avatar"
            />
          </div>
          <div className="cm_instagram_pill_info">
            <span className="cm_instagram_pill_subtitle">
              {isIncomingVideoCall ? 'Incoming Video Call' : 'Incoming Voice Call'}
            </span>
            <span className="cm_instagram_pill_title">{opponentPillUsername}</span>
            {showTapForSoundHint && (
              <span className="cm_instagram_pill_price" role="status">
                Tap to enable ringtone
              </span>
            )}
            {!showTapForSoundHint && insufficientCreditsForIncoming && creditsRequiredToAccept != null ? (
              <span className="cm_instagram_pill_credits_needed" role="alert">
                Add {formatCreditsValue(creditsRequiredToAccept)} credits to accept this call.
              </span>
            ) : null}
            {!showTapForSoundHint && !insufficientCreditsForIncoming && incomingPricingSummary ? (
              <span className="cm_instagram_pill_price" title={incomingPricingSummary}>
                {incomingPricingSummary}
              </span>
            ) : null}
          </div>
        </div>

        {!insufficientCreditsForIncoming && (
          <div className="cm_instagram_pill_actions">
            <button
              type="button"
              className="cm_instagram_btn_reject"
              onClick={handleReject}
              aria-label="Reject call"
            >
              <i
                className={`fa-solid fa-${isIncomingVideoCall ? 'xmark' : 'phone-slash'}`}
                style={{ fontSize: isIncomingVideoCall ? 24 : 18 }}
                aria-hidden
              />
            </button>

            <button
              type="button"
              className={`cm_instagram_btn_answer${!canAnswer ? ' opacity-50' : ''}`}
              onClick={handleAnswer}
              disabled={!canAnswer}
              aria-busy={answerButtonCreditsPending}
              aria-label={
                answerButtonCreditsPending
                  ? 'Verifying credits'
                  : `Answer ${isIncomingVideoCall ? 'video' : 'audio'} call`
              }
              aria-disabled={!canAnswer}
            >
              {answerButtonCreditsPending ? (
                <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 20 }} aria-hidden />
              ) : (
                <i
                  className={`fa-solid fa-${isIncomingVideoCall ? 'video' : 'phone'}`}
                  style={{ fontSize: isIncomingVideoCall ? 19 : 18 }}
                  aria-hidden
                />
              )}
            </button>
          </div>
        )}
      </div>

      {isSubscriber &&
        incomingPricingResult.ok &&
        !walletBalancePending &&
        !Number.isFinite(walletCredits) && (
          <div className="cm_instagram_pill_warning_text text-danger mt-3" role="alert">
            {walletBalanceError
              ? String(walletBalanceError)
              : 'Could not read your credit balance.'}
          </div>
        )}
    </Modal>
  );
};

export default IncomingCallModal;
